import { type FC, memo, useEffect, useRef, useState, useCallback, type RefObject, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CampaignSource,
  PRIZE_POP_CAMPAIGN_TYPE,
  type PrizePopInterval,
  useSelectedCampaign,
  useCampaigns,
  usePanelInfo,
} from 'features/campaigns';
import type { CampaignPanelInfo } from 'features/campaigns/components/MultiViewCampaignSection';
import {
  ZERO_TIME,
  getTimeDiffFormatted,
  createCountdownResult,
  type CountdownDirection,
  type CountdownResult,
} from '../../countdownHelpers';

export type { CountdownDirection };

// Helper: Get start or end of day in UTC
const getUTCDayBoundary = (date: Date, end: boolean = false): Date => {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      end ? 23 : 0,
      end ? 59 : 0,
      end ? 59 : 0,
      end ? 999 : 0,
    ),
  );
};

// Parse UTC time string and apply time components to baseDate
const parseSlotTime = (timeStr: string, baseDate: Date): Date => {
  let hour: number, min: number, sec: number;

  if (timeStr.includes('T')) {
    const parsed = new Date(timeStr);
    hour = parsed.getUTCHours();
    min = parsed.getUTCMinutes();
    sec = parsed.getUTCSeconds();
  } else {
    const timeOnly = timeStr.replace('Z', '');
    [hour, min, sec] = timeOnly.split(':').map(Number);
  }

  return new Date(
    Date.UTC(
      baseDate.getUTCFullYear(),
      baseDate.getUTCMonth(),
      baseDate.getUTCDate(),
      hour,
      min,
      sec,
      0,
    ),
  );
};

/**
 * Calculates the current countdown info from campaign intervals.
 * `campaign` covers every source `useCampaignForTimer` can resolve to — only
 * `IPrizePopCampaign` and `CampaignPanelInfo` actually carry `intervals`, the rest don't.
 */
const calculateCountdown = (
  campaign: CampaignSource | CampaignPanelInfo | undefined,
  t: (key: string, fallback: string) => string,
): CountdownResult => {
  const intervals: PrizePopInterval[] | undefined =
    campaign && 'intervals' in campaign ? campaign.intervals : undefined;

  if (!intervals || intervals.length === 0) {
    return {
      label: t('campaigns.starts_in', 'Starts In'),
      value: ZERO_TIME,
      direction: 'startsIn',
      isTicking: true,
    };
  }

  const now = new Date();

  // Iterate through all campaign intervals to find active/upcoming slots
  for (const interval of intervals) {
    const rangeStart = new Date(interval.range.startRangeDate);
    const rangeEnd = new Date(interval.range.endRangeDate);
    const hoursRange = interval.hoursRange || [];

    // Skip intervals that have completely ended
    if (now > rangeEnd) {
      continue;
    }

    // Handle intervals without hour slots (all-day campaign)
    if (hoursRange.length === 0) {
      if (now >= rangeStart && now <= rangeEnd) {
        const endOfDay = getUTCDayBoundary(rangeEnd, true);
        return createCountdownResult(now, endOfDay, 'endsIn', t);
      }
      if (now < rangeStart) {
        const startOfDay = getUTCDayBoundary(rangeStart, false);
        return createCountdownResult(now, startOfDay, 'startsIn', t);
      }
    }

    // Iterate through each day in the interval range
    let currentDay = getUTCDayBoundary(rangeStart, false);
    const endDay = getUTCDayBoundary(rangeEnd, true);

    while (currentDay <= endDay) {
      for (const slot of hoursRange) {
        // UTC ISO logic: parseSlotTime extracts the time component from the ISO string and applies it to the current day
        const slotStart = parseSlotTime(slot.startTime, currentDay);
        const slotEnd = parseSlotTime(slot.endTime, currentDay);

        // Check if currently inside this slot
        if (now >= slotStart && now <= slotEnd) {
          return {
            label: t('campaigns.ends_in', 'Ends In'),
            value: getTimeDiffFormatted(now, slotEnd),
            direction: 'endsIn',
            isTicking: true,
          };
        }

        // Check if this slot is upcoming
        if (now < slotStart) {
          return createCountdownResult(now, slotStart, 'startsIn', t);
        }
      }
      // Move to next day in UTC
      const nextDay = new Date(currentDay.getTime() + 24 * 60 * 60 * 1000);
      currentDay = getUTCDayBoundary(nextDay, false);
    }
  }

  // No active/upcoming slots found - search for the nearest future start time
  let nextStart: Date | null = null;
  let minDiff = Infinity;
  // Check all future intervals for the nearest slot
  for (const interval of intervals) {
    const rangeStart = new Date(interval.range.startRangeDate);
    const hoursRange = interval.hoursRange || [];

    if (now < rangeStart) {
      if (hoursRange.length > 0) {
        const firstSlot = hoursRange[0];
        const firstSlotStart = parseSlotTime(firstSlot.startTime, rangeStart);
        const diff = firstSlotStart.getTime() - now.getTime();
        if (diff > 0 && diff < minDiff) {
          minDiff = diff;
          nextStart = firstSlotStart;
        }
      } else {
        const diff = rangeStart.getTime() - now.getTime();
        if (diff > 0 && diff < minDiff) {
          minDiff = diff;
          nextStart = rangeStart;
        }
      }
    }
  }

  if (nextStart) {
    return createCountdownResult(now, nextStart, 'startsIn', t);
  }

  // All intervals finished
  return {
    label: t('campaigns.status', 'Status'),
    value: t('campaigns.finished', 'Finished'),
    direction: 'endsIn',
    isTicking: false,
  };
};

const useCampaignForTimer = (campaignId?: string) => {
  const selectedCampaign = useSelectedCampaign(PRIZE_POP_CAMPAIGN_TYPE);
  const campaigns = useCampaigns(PRIZE_POP_CAMPAIGN_TYPE);
  const panelInfo = usePanelInfo(PRIZE_POP_CAMPAIGN_TYPE);

  return campaignId === 'panel'
    ? panelInfo
    : campaignId
      ? campaigns.find(c => c.id === campaignId)
      : selectedCampaign;
};

interface CountdownTimerProps {
  campaignId?: string;
  /** Render prop: receives { label, valueRef, direction, initialValue } for full control over markup */
  children: (props: {
    label: string;
    valueRef: RefObject<HTMLSpanElement | null>;
    direction: CountdownDirection;
    initialValue: string;
  }) => ReactNode;
}

/**
 * Self-contained countdown timer that updates the DOM directly via ref.
 * Only causes React re-renders when the label or direction changes
 * (e.g., "Starts In" → "Ends In", or countdown → finished).
 * The ticking seconds update is done via ref.current.textContent — zero React re-renders.
 *
 * Uses a render prop pattern so consumers control the markup/styling.
 */
const IntervalCountdownTimer: FC<CountdownTimerProps> = ({ campaignId, children }) => {
  const { t } = useTranslation();
  const campaign = useCampaignForTimer(campaignId);

  const valueRef = useRef<HTMLSpanElement>(null);
  const prevLabelRef = useRef('');
  const prevDirectionRef = useRef<CountdownDirection>('startsIn');

  const getResult = useCallback(() => calculateCountdown(campaign, t), [campaign, t]);

  // React state only for label/direction changes (rare transitions)
  const [state, setState] = useState(() => {
    const result = getResult();
    return { label: result.label, direction: result.direction, isTicking: result.isTicking };
  });

  useEffect(() => {
    const tick = () => {
      const result = getResult();

      // Update DOM directly for the ticking value — no React re-render
      if (valueRef.current) {
        valueRef.current.textContent = result.value;
      }

      // Only trigger React re-render when label or direction changes
      if (prevLabelRef.current !== result.label || prevDirectionRef.current !== result.direction) {
        prevLabelRef.current = result.label;
        prevDirectionRef.current = result.direction;
        setState({ label: result.label, direction: result.direction, isTicking: result.isTicking });
      }
    };

    tick();

    if (!state.isTicking) return;

    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [getResult, state.isTicking]);

  const initialValue = getResult().value;

  return <>{children({ label: state.label, valueRef, direction: state.direction, initialValue })}</>;
};

export default memo(IntervalCountdownTimer);
